import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { compressImage, deleteStorageImage } from '../lib/imageUtils';
import { normalizeItemCode, isValidItemCode } from '../lib/validation';
import type { Profile, Organization } from '../types';

export type ItemStatus = 'available' | 'loaned' | 'pending';

export interface InventoryItem {
  id: string;
  user_id: string;
  navn: string;
  kategori: string;
  antall: number;
  enhet: string;
  beskrivelse: string;
  bilde_url: string;
  emoji: string;
  list_id: string | null;
  created_at: string;
  // Utlånsfelt
  loanable: boolean;
  status: ItemStatus;
  loaned_to_id: string | null;
  loaned_at: string | null;
  due_date: string | null;
  overdue_notified_at: string | null;
  approval_message: string | null;
  return_message: string | null;
  // Personlige tagger (fritekst, flere per item)
  personal_tags: string[];
  // Satt til true kun for ting lagt til via "Rask registrering" — skiller dem fra
  // vanlige ting uten at fravær av bilde alene skal bety noe
  quick_added?: boolean;
  // Filterkategorier (gruppe-kategorier dette item er lenket til)
  filterCategories?: { id: string; name: string; emoji: string }[];
  // Valgfrie joined-felt (kun i gruppe-visning)
  owner?: Profile;
  loaned_to?: Profile;
  // Satt hvis tingen er koblet til en organisasjon via organization_items —
  // er dette satt, eies tingen kollektivt av organisasjonen, ikke personlig
  // av user_id (som fortsatt kun er "registrert av"/kontaktperson)
  organization?: Organization | null;
  // Navn på alle grupper tingen er delt i (via inventory_groups) — kun satt i
  // fetchLoanableItems, brukes til gruppe-ikonet på kortet i Oversikt
  groupNames?: string[];
}

export interface InventoryList {
  id: string;
  user_id: string;
  name: string;
  emoji: string;
  share_token: string | null;
  created_at: string;
  is_favorite: boolean;
}

export interface ItemCodeEntry {
  code: string;
  listId: string | null;
  inventoryId: string | null;
}

export interface ShareAccess {
  shareId: string;
  userId: string;
  name: string;
  color: string;
  listId: string;
  listName: string;
}

export interface SharedInventory {
  shareId: string;
  ownerId: string;
  ownerName: string;
  ownerColor: string;
  listId: string;
  listName: string;
  listEmoji: string;
  items: InventoryItem[];
}

interface InventoryState {
  items: InventoryItem[];
  lists: InventoryList[];
  itemCodes: ItemCodeEntry[];
  itemCodesLoaded: boolean;
  loading: boolean;
  sharedWith: ShareAccess[];
  sharedInventories: SharedInventory[];
  // Persistert tagg-katalog (fra profiles.saved_tags)
  savedTags: string[];
  // Gruppe-utlån
  loanableItems: InventoryItem[];
  myLoans: InventoryItem[];
  myLentOut: InventoryItem[];

  fetchItems: (userId: string) => Promise<void>;
  fetchSavedTags: (userId: string) => Promise<void>;
  addSavedTags: (userId: string, newTags: string[]) => Promise<void>;
  addItem: (data: Omit<InventoryItem, 'id' | 'created_at' | 'owner' | 'loaned_to'>) => Promise<InventoryItem | null>;
  updateItem: (id: string, data: Partial<Omit<InventoryItem, 'owner' | 'loaned_to'>>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  uploadImage: (file: File) => Promise<string | null>;

  fetchLists: (userId: string) => Promise<void>;
  createList: (name: string, userId: string, itemIds: string[], emoji?: string) => Promise<void>;
  renameList: (listId: string, name: string, emoji?: string) => Promise<void>;
  deleteList: (listId: string) => Promise<void>;
  assignToList: (itemId: string, listId: string | null) => Promise<void>;
  toggleListFavorite: (listId: string, isFavorite: boolean) => Promise<void>;

  fetchItemCodes: (userId: string) => Promise<void>;
  setListCode: (userId: string, listId: string, rawCode: string | null) => Promise<string | null>;
  setItemCode: (userId: string, inventoryId: string, rawCode: string | null) => Promise<string | null>;
  resolveItemCode: (rawCode: string) => { type: 'list' | 'item'; id: string } | null;

  fetchShareData: (userId: string) => Promise<void>;
  generateShareToken: (listId: string) => Promise<string>;
  joinByToken: (token: string, userId: string) => Promise<{ ok: boolean; message: string }>;
  revokeShare: (shareId: string) => Promise<void>;
  leaveShare: (shareId: string) => Promise<void>;
  sendShareEmail: (toEmail: string, senderName: string, shareToken: string) => Promise<string | null>;

  // Gruppe-visning
  fetchLoanableItems: (groupId: string) => Promise<void>;
  fetchMyLoans: (userId: string) => Promise<void>;
  fetchMyLentOut: (userId: string) => Promise<void>;
  fetchInventoryGroups: (inventoryId: string) => Promise<string[]>;
  addInventoryToGroup: (inventoryId: string, groupId: string) => Promise<void>;
  removeInventoryFromGroup: (inventoryId: string, groupId: string) => Promise<void>;
  // Filterkategorier
  fetchItemFilterCategories: (inventoryId: string) => Promise<{ id: string; name: string; emoji: string }[]>;
  addItemFilterCategory: (inventoryId: string, categoryId: string) => Promise<void>;
  removeItemFilterCategory: (inventoryId: string, categoryId: string) => Promise<void>;
  // Tagg-administrasjon
  renamePersonalTag: (userId: string, oldTag: string, newTag: string) => Promise<void>;
  deletePersonalTag: (userId: string, tag: string, replaceWith?: string) => Promise<void>;

  // Utlånsflyt
  requestLoanItem: (inventoryId: string, dueDate?: string) => Promise<string | null>;
  approveLoanItem: (inventoryId: string, message?: string) => Promise<string | null>;
  rejectLoanItem: (inventoryId: string) => Promise<string | null>;
  cancelLoanItem: (inventoryId: string) => Promise<string | null>;
  returnItem: (inventoryId: string, message?: string) => Promise<string | null>;
  loanOutItem: (inventoryId: string, toUserId: string, dueDate?: string) => Promise<string | null>;
  subscribeToLoanStatus: (userId: string) => () => void;
  handleNotification: (type: string, inventoryId: string | null, fromUserId?: string | null) => void;
}

async function enrichWithProfiles(items: any[]): Promise<InventoryItem[]> {
  if (!items.length) return [];
  const ids = [...new Set([
    ...items.map(i => i.user_id),
    ...items.filter(i => i.loaned_to_id).map(i => i.loaned_to_id),
  ])];
  const { data: profiles } = await supabase.from('profiles').select('id, full_name, avatar_color, email, created_at').in('id', ids);
  const map = new Map((profiles || []).map((p: any) => [p.id, p]));

  // Er tingen koblet til en organisasjon (organization_items), eies den
  // kollektivt av organisasjonen — user_id forblir kun "registrert av".
  const itemIds = items.map(i => i.id).filter(Boolean);
  const { data: orgLinks } = itemIds.length
    ? await supabase.from('organization_items').select('inventory_id, organizations(id, name, type, invite_code, created_by, created_at)').in('inventory_id', itemIds)
    : { data: [] as any[] };
  const orgMap = new Map<string, any>();
  (orgLinks || []).forEach((l: any) => { if (l.organizations) orgMap.set(l.inventory_id, l.organizations); });

  return items.map(item => ({
    ...item,
    owner: map.get(item.user_id),
    loaned_to: item.loaned_to_id ? map.get(item.loaned_to_id) : undefined,
    organization: orgMap.get(item.id) || null,
  }));
}

export const useInventory = create<InventoryState>((set, get) => ({
  items: [],
  lists: [],
  itemCodes: [],
  itemCodesLoaded: false,
  loading: false,
  sharedWith: [],
  sharedInventories: [],
  savedTags: [],
  loanableItems: [],
  myLoans: [],
  myLentOut: [],

  fetchItems: async (userId) => {
    set({ loading: true });
    const { data } = await supabase
      .from('inventory')
      .select('*, inventory_category_links(category_id, categories(id, name, emoji))')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    const raw = (data || []).map((item: any) => ({
      ...item,
      filterCategories: (item.inventory_category_links || []).map((l: any) => l.categories).filter(Boolean),
      inventory_category_links: undefined,
    }));
    const items = await enrichWithProfiles(raw);
    set({ items, loading: false });
  },

  fetchSavedTags: async (userId) => {
    const { data } = await supabase.from('profiles').select('saved_tags').eq('id', userId).single();
    set({ savedTags: ((data?.saved_tags as string[]) || []).filter(Boolean).sort() });
  },

  addSavedTags: async (userId, newTags) => {
    const toAdd = newTags.filter(t => t && !get().savedTags.includes(t));
    if (!toAdd.length) return;
    const merged = [...new Set([...get().savedTags, ...toAdd])].sort();
    await supabase.from('profiles').update({ saved_tags: merged }).eq('id', userId);
    set({ savedTags: merged });
  },

  addItem: async (data) => {
    const { data: row, error } = await supabase.from('inventory').insert(data).select().single();
    if (error) throw new Error(error.message);
    if (row) set({ items: [row, ...get().items] });
    return row || null;
  },

  updateItem: async (id, data) => {
    if ('bilde_url' in data) {
      const { data: old } = await supabase.from('inventory').select('bilde_url').eq('id', id).single();
      if (old?.bilde_url && old.bilde_url !== data.bilde_url) await deleteStorageImage(old.bilde_url);
    }
    const { data: row } = await supabase.from('inventory').update(data).eq('id', id).select().single();
    if (row) {
      const loanableTurnedOff = 'loanable' in data && data.loanable === false;
      set({
        items: get().items.map(i => i.id === id ? { ...i, ...row } : i),
        loanableItems: loanableTurnedOff
          ? get().loanableItems.filter(i => i.id !== id)
          : get().loanableItems.map(i => i.id === id ? { ...i, ...row } : i),
      });
    }
  },

  deleteItem: async (id) => {
    const { data: item } = await supabase.from('inventory').select('bilde_url').eq('id', id).single();
    await deleteStorageImage(item?.bilde_url);
    await supabase.from('inventory').delete().eq('id', id);
    set({
      items: get().items.filter(i => i.id !== id),
      loanableItems: get().loanableItems.filter(i => i.id !== id),
      itemCodes: get().itemCodes.filter(c => c.inventoryId !== id),
    });
  },

  fetchLists: async (userId) => {
    const { data } = await supabase.from('inventory_lists').select('*').eq('user_id', userId).order('created_at');
    set({ lists: data || [] });
  },

  createList: async (name, userId, itemIds, emoji = '') => {
    const { data: list, error } = await supabase.from('inventory_lists').insert({ name, user_id: userId, emoji }).select().single();
    if (error) throw new Error(error.message);
    if (!list) throw new Error('Ingen data returnert');
    if (itemIds.length > 0) {
      await supabase.from('inventory').update({ list_id: list.id }).in('id', itemIds);
      set({ items: get().items.map(i => itemIds.includes(i.id) ? { ...i, list_id: list.id } : i) });
    }
    set({ lists: [...get().lists, list] });
  },

  renameList: async (listId, name, emoji) => {
    const update: any = { name };
    if (emoji !== undefined) update.emoji = emoji;
    await supabase.from('inventory_lists').update(update).eq('id', listId);
    set({ lists: get().lists.map(l => l.id === listId ? { ...l, name, ...(emoji !== undefined ? { emoji } : {}) } : l) });
  },

  toggleListFavorite: async (listId, isFavorite) => {
    await supabase.from('inventory_lists').update({ is_favorite: isFavorite }).eq('id', listId);
    set({ lists: get().lists.map(l => l.id === listId ? { ...l, is_favorite: isFavorite } : l) });
  },

  deleteList: async (listId) => {
    await supabase.from('inventory_lists').delete().eq('id', listId);
    set({
      lists: get().lists.filter(l => l.id !== listId),
      items: get().items.map(i => i.list_id === listId ? { ...i, list_id: null } : i),
      itemCodes: get().itemCodes.filter(c => c.listId !== listId),
    });
  },

  assignToList: async (itemId, listId) => {
    await supabase.from('inventory').update({ list_id: listId }).eq('id', itemId);
    set({ items: get().items.map(i => i.id === itemId ? { ...i, list_id: listId } : i) });
  },

  fetchItemCodes: async (userId) => {
    const { data } = await supabase.from('item_codes').select('code, list_id, inventory_id').eq('user_id', userId);
    set({
      itemCodes: (data || []).map((r: any) => ({ code: r.code, listId: r.list_id, inventoryId: r.inventory_id })),
      itemCodesLoaded: true,
    });
  },

  setListCode: async (userId, listId, rawCode) => {
    const code = rawCode ? normalizeItemCode(rawCode) : '';
    if (!code) {
      await supabase.from('item_codes').delete().eq('user_id', userId).eq('list_id', listId);
      set({ itemCodes: get().itemCodes.filter(c => c.listId !== listId) });
      return null;
    }
    if (!isValidItemCode(code)) return 'Ugyldig kode (bruk bokstaver/tall, maks 12 tegn)';
    const { error } = await supabase.from('item_codes')
      .upsert({ user_id: userId, list_id: listId, inventory_id: null, code }, { onConflict: 'user_id,list_id' });
    if (error) return error.code === '23505' ? 'Denne koden er allerede i bruk' : 'Noe gikk galt, prøv igjen';
    set({ itemCodes: [...get().itemCodes.filter(c => c.listId !== listId), { code, listId, inventoryId: null }] });
    return null;
  },

  setItemCode: async (userId, inventoryId, rawCode) => {
    const code = rawCode ? normalizeItemCode(rawCode) : '';
    if (!code) {
      await supabase.from('item_codes').delete().eq('user_id', userId).eq('inventory_id', inventoryId);
      set({ itemCodes: get().itemCodes.filter(c => c.inventoryId !== inventoryId) });
      return null;
    }
    if (!isValidItemCode(code)) return 'Ugyldig kode (bruk bokstaver/tall, maks 12 tegn)';
    const { error } = await supabase.from('item_codes')
      .upsert({ user_id: userId, list_id: null, inventory_id: inventoryId, code }, { onConflict: 'user_id,inventory_id' });
    if (error) return error.code === '23505' ? 'Denne koden er allerede i bruk' : 'Noe gikk galt, prøv igjen';
    set({ itemCodes: [...get().itemCodes.filter(c => c.inventoryId !== inventoryId), { code, listId: null, inventoryId }] });
    return null;
  },

  resolveItemCode: (rawCode) => {
    const code = normalizeItemCode(rawCode);
    if (!code) return null;
    const entry = get().itemCodes.find(c => c.code === code);
    if (!entry) return null;
    return entry.listId ? { type: 'list', id: entry.listId } : { type: 'item', id: entry.inventoryId! };
  },

  uploadImage: async (file) => {
    if (!file.type.startsWith('image/')) throw new Error('Bare bildefiler er tillatt');
    if (file.size > 10 * 1024 * 1024) throw new Error('Bildet er for stort (maks 10 MB)');
    const compressed = await compressImage(file);
    const path = `inventory/${Date.now()}.jpg`;
    const { error } = await supabase.storage.from('tool-images').upload(path, compressed);
    if (error) return null;
    const { data } = supabase.storage.from('tool-images').getPublicUrl(path);
    return data.publicUrl;
  },

  fetchShareData: async (userId) => {
    const [{ data: given }, { data: received }] = await Promise.all([
      supabase.from('inventory_shares')
        .select('id, shared_with_id, list_id, inventory_lists(name)')
        .eq('owner_id', userId),
      supabase.from('inventory_shares')
        .select('id, owner_id, list_id, inventory_lists(name, emoji)')
        .eq('shared_with_id', userId),
    ]);

    // Hvem har tilgang til mine lister
    let sharedWith: ShareAccess[] = [];
    if (given && given.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles').select('id, full_name, avatar_color')
        .in('id', given.map((g: any) => g.shared_with_id));
      sharedWith = given.map((g: any) => {
        const p = profiles?.find((pr: any) => pr.id === g.shared_with_id);
        return {
          shareId: g.id,
          userId: g.shared_with_id,
          name: p?.full_name || 'Ukjent',
          color: p?.avatar_color || 'green',
          listId: g.list_id,
          listName: (g.inventory_lists as any)?.name || 'Ukjent liste',
        };
      });
    }

    // Lister jeg følger
    let sharedInventories: SharedInventory[] = [];
    if (received && received.length > 0) {
      const { data: ownerProfiles } = await supabase
        .from('profiles').select('id, full_name, avatar_color')
        .in('id', received.map((r: any) => r.owner_id));

      sharedInventories = await Promise.all(
        received.map(async (share: any) => {
          const listInfo = share.inventory_lists as any;
          const { data: listItems } = await supabase
            .from('inventory').select('*')
            .eq('list_id', share.list_id)
            .order('created_at', { ascending: false });
          const p = ownerProfiles?.find((pr: any) => pr.id === share.owner_id);
          return {
            shareId: share.id,
            ownerId: share.owner_id,
            ownerName: p?.full_name || 'Ukjent',
            ownerColor: p?.avatar_color || 'green',
            listId: share.list_id,
            listName: listInfo?.name || 'Ukjent liste',
            listEmoji: listInfo?.emoji || '',
            items: listItems || [],
          };
        })
      );
    }

    set({ sharedWith, sharedInventories });
  },

  generateShareToken: async (listId) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const token = Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map(b => chars[b % chars.length])
      .join('');
    await supabase.from('inventory_lists').update({ share_token: token }).eq('id', listId);
    set({ lists: get().lists.map(l => l.id === listId ? { ...l, share_token: token } : l) });
    return token;
  },

  joinByToken: async (token, userId) => {
    const { data: list } = await supabase
      .from('inventory_lists').select('id, user_id')
      .eq('share_token', token.trim().toUpperCase())
      .single();

    if (!list) return { ok: false, message: 'Fant ingen liste med denne koden' };
    if (list.user_id === userId) return { ok: false, message: 'Du kan ikke legge til din egen liste' };

    const { error } = await supabase
      .from('inventory_shares')
      .insert({ owner_id: list.user_id, shared_with_id: userId, list_id: list.id });

    if (error) {
      if (error.code === '23505') return { ok: false, message: 'Du følger allerede denne listen' };
      return { ok: false, message: 'Noe gikk galt, prøv igjen' };
    }
    return { ok: true, message: '' };
  },

  revokeShare: async (shareId) => {
    await supabase.from('inventory_shares').delete().eq('id', shareId);
    set({ sharedWith: get().sharedWith.filter(s => s.shareId !== shareId) });
  },

  leaveShare: async (shareId) => {
    await supabase.from('inventory_shares').delete().eq('id', shareId);
    set({ sharedInventories: get().sharedInventories.filter(s => s.shareId !== shareId) });
  },

  sendShareEmail: async (toEmail, senderName, shareToken) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('https://hzplqizxzdwmfdopedjf.supabase.co/functions/v1/send-inventory-share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ to: toEmail, senderName, shareToken }),
      });
      const data = await res.json();
      if (!res.ok) return data.error || 'Sending feilet';
      return null;
    } catch {
      return 'Noe gikk galt';
    }
  },

  // ─── Gruppe-visning ───────────────────────────────────────

  fetchLoanableItems: async (groupId) => {
    set({ loanableItems: [] });
    const { data: groupLinks } = await supabase
      .from('inventory_groups').select('inventory_id').eq('group_id', groupId);
    if (!groupLinks?.length) { set({ loanableItems: [] }); return; }
    const ids = groupLinks.map((g: any) => g.inventory_id);
    const [{ data: rawItems }, { data: allGroupLinks }] = await Promise.all([
      supabase
        .from('inventory')
        .select('*, inventory_category_links(category_id, categories(id, name, emoji))')
        .eq('loanable', true)
        .in('id', ids),
      // Alle grupper hvert item er delt i (ikke bare den aktive gruppen) —
      // brukes til gruppe-ikonet på kortet i Oversikt, viktig for ting delt i flere grupper samtidig.
      supabase.from('inventory_groups').select('inventory_id, groups(name)').in('inventory_id', ids),
    ]);
    const groupNameMap = new Map<string, string[]>();
    (allGroupLinks || []).forEach((r: any) => {
      const name = r.groups?.name;
      if (!name) return;
      if (!groupNameMap.has(r.inventory_id)) groupNameMap.set(r.inventory_id, []);
      groupNameMap.get(r.inventory_id)!.push(name);
    });
    const processed = (rawItems || []).map((item: any) => ({
      ...item,
      filterCategories: (item.inventory_category_links || []).map((l: any) => l.categories).filter(Boolean),
      inventory_category_links: undefined,
      groupNames: groupNameMap.get(item.id) || [],
    }));
    set({ loanableItems: await enrichWithProfiles(processed) });
  },

  fetchMyLoans: async (userId) => {
    const { data } = await supabase
      .from('inventory').select('*')
      .eq('loaned_to_id', userId)
      .in('status', ['loaned', 'pending']);
    const enriched = await enrichWithProfiles(data || []);
    // Hent hvilke grupper hvert item er i
    const itemIds = enriched.map(i => i.id);
    if (itemIds.length > 0) {
      const { data: grpLinks } = await supabase.from('inventory_groups').select('inventory_id, group_id').in('inventory_id', itemIds);
      const grpMap = new Map<string, string[]>();
      (grpLinks || []).forEach((l: any) => {
        if (!grpMap.has(l.inventory_id)) grpMap.set(l.inventory_id, []);
        grpMap.get(l.inventory_id)!.push(l.group_id);
      });
      set({ myLoans: enriched.map(i => ({ ...i, groups: (grpMap.get(i.id) || []) as any })) });
    } else {
      set({ myLoans: [] });
    }
  },

  fetchMyLentOut: async (userId) => {
    const { data } = await supabase
      .from('inventory').select('*')
      .eq('user_id', userId)
      .eq('status', 'loaned');
    set({ myLentOut: await enrichWithProfiles(data || []) });
  },

  fetchInventoryGroups: async (inventoryId) => {
    const { data } = await supabase
      .from('inventory_groups').select('group_id').eq('inventory_id', inventoryId);
    return (data || []).map((g: any) => g.group_id);
  },

  addInventoryToGroup: async (inventoryId, groupId) => {
    await supabase.from('inventory_groups').insert({ inventory_id: inventoryId, group_id: groupId });
  },

  removeInventoryFromGroup: async (inventoryId, groupId) => {
    await supabase.from('inventory_groups').delete()
      .eq('inventory_id', inventoryId).eq('group_id', groupId);
  },

  fetchItemFilterCategories: async (inventoryId) => {
    const { data } = await supabase
      .from('inventory_category_links')
      .select('category_id, categories(id, name, emoji)')
      .eq('inventory_id', inventoryId);
    return (data || []).map((l: any) => l.categories).filter(Boolean);
  },

  addItemFilterCategory: async (inventoryId, categoryId) => {
    await supabase.from('inventory_category_links').insert({ inventory_id: inventoryId, category_id: categoryId });
  },

  removeItemFilterCategory: async (inventoryId, categoryId) => {
    await supabase.from('inventory_category_links')
      .delete().eq('inventory_id', inventoryId).eq('category_id', categoryId);
  },

  renamePersonalTag: async (userId, oldTag, newTag) => {
    const { data } = await supabase
      .from('inventory').select('id, personal_tags')
      .eq('user_id', userId).contains('personal_tags', [oldTag]);
    await Promise.all((data || []).map((item: any) =>
      supabase.from('inventory').update({
        personal_tags: item.personal_tags.map((t: string) => t === oldTag ? newTag : t),
      }).eq('id', item.id)
    ));
    const updatedSavedTags = get().savedTags.map(t => t === oldTag ? newTag : t).sort();
    await supabase.from('profiles').update({ saved_tags: updatedSavedTags }).eq('id', userId);
    set({
      savedTags: updatedSavedTags,
      items: get().items.map(i => ({
        ...i,
        personal_tags: (i.personal_tags ?? []).map(t => t === oldTag ? newTag : t),
      })),
    });
  },

  deletePersonalTag: async (userId, tag, replaceWith) => {
    const { data } = await supabase
      .from('inventory').select('id, personal_tags')
      .eq('user_id', userId).contains('personal_tags', [tag]);
    await Promise.all((data || []).map((item: any) => {
      let newTags: string[] = item.personal_tags.filter((t: string) => t !== tag);
      if (replaceWith && !newTags.includes(replaceWith)) newTags = [...newTags, replaceWith];
      return supabase.from('inventory').update({ personal_tags: newTags }).eq('id', item.id);
    }));
    const updatedSavedTags = get().savedTags.filter(t => t !== tag);
    await supabase.from('profiles').update({ saved_tags: updatedSavedTags }).eq('id', userId);
    set({
      savedTags: updatedSavedTags,
      items: get().items.map(i => {
        let newTags = (i.personal_tags ?? []).filter(t => t !== tag);
        if (replaceWith && !newTags.includes(replaceWith)) newTags = [...newTags, replaceWith];
        return { ...i, personal_tags: newTags };
      }),
    });
  },

  // ─── Utlånsflyt ──────────────────────────────────────────

  requestLoanItem: async (inventoryId, dueDate) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.rpc('request_loan_item', {
      p_inventory_id: inventoryId,
      p_due_date: dueDate || null,
    });
    if (error) return error.message;
    const loanableItem = get().loanableItems.find(i => i.id === inventoryId);
    const pendingItem = loanableItem
      ? { ...loanableItem, status: 'pending' as ItemStatus, loaned_to_id: user?.id ?? null }
      : null;
    set({
      loanableItems: get().loanableItems.map(i =>
        i.id === inventoryId ? { ...i, status: 'pending' as ItemStatus } : i),
      myLoans: pendingItem
        ? [...get().myLoans.filter(i => i.id !== inventoryId), pendingItem]
        : get().myLoans,
    });
    return null;
  },

  approveLoanItem: async (inventoryId, message) => {
    const { error } = await supabase.rpc('approve_loan_item', { p_inventory_id: inventoryId, p_message: message || null });
    if (error) return error.message;
    set({
      loanableItems: get().loanableItems.map(i =>
        i.id === inventoryId ? { ...i, status: 'loaned' as ItemStatus } : i),
      items: get().items.map(i =>
        i.id === inventoryId ? { ...i, status: 'loaned' as ItemStatus, approval_message: message || null } : i),
    });
    const { data: { user } } = await supabase.auth.getUser();
    if (user) await get().fetchMyLentOut(user.id);
    return null;
  },

  rejectLoanItem: async (inventoryId) => {
    const { error } = await supabase.rpc('reject_loan_item', { p_inventory_id: inventoryId });
    if (error) return error.message;
    set({
      loanableItems: get().loanableItems.map(i =>
        i.id === inventoryId ? { ...i, status: 'available' as ItemStatus, loaned_to_id: null } : i),
      items: get().items.map(i =>
        i.id === inventoryId ? { ...i, status: 'available' as ItemStatus, loaned_to_id: null } : i),
      myLoans: get().myLoans.filter(i => i.id !== inventoryId),
    });
    return null;
  },

  cancelLoanItem: async (inventoryId) => {
    const { error } = await supabase.rpc('cancel_loan_item', { p_inventory_id: inventoryId });
    if (error) return error.message;
    set({ myLoans: get().myLoans.filter(i => i.id !== inventoryId) });
    return null;
  },

  returnItem: async (inventoryId, message) => {
    const { error } = await supabase.rpc('return_item', { p_inventory_id: inventoryId, p_message: message || null });
    if (error) return error.message;
    const reset = { status: 'available' as ItemStatus, loaned_to_id: null, loaned_at: null, due_date: null, approval_message: null, return_message: message || null };
    set({
      loanableItems: get().loanableItems.map(i => i.id === inventoryId ? { ...i, ...reset } : i),
      myLoans: get().myLoans.filter(i => i.id !== inventoryId),
      myLentOut: get().myLentOut.filter(i => i.id !== inventoryId),
      items: get().items.map(i => i.id === inventoryId ? { ...i, ...reset } : i),
    });
    return null;
  },

  loanOutItem: async (inventoryId, toUserId, dueDate) => {
    const { error } = await supabase.from('inventory').update({
      status: 'loaned',
      loaned_to_id: toUserId,
      loaned_at: new Date().toISOString(),
      due_date: dueDate || null,
    }).eq('id', inventoryId);
    if (error) return error.message;
    set({
      loanableItems: get().loanableItems.map(i =>
        i.id === inventoryId ? { ...i, status: 'loaned' as ItemStatus, loaned_to_id: toUserId } : i),
      items: get().items.map(i =>
        i.id === inventoryId ? { ...i, status: 'loaned' as ItemStatus, loaned_to_id: toUserId } : i),
    });
    return null;
  },

  handleNotification: (type, inventoryId, fromUserId = null) => {
    if (!inventoryId) return;
    if (type === 'loan_approved') {
      set(state => ({
        myLoans: state.myLoans.map(i =>
          i.id === inventoryId ? { ...i, status: 'loaned' as ItemStatus } : i),
        loanableItems: state.loanableItems.map(i =>
          i.id === inventoryId ? { ...i, status: 'loaned' as ItemStatus } : i),
      }));
    } else if (type === 'loan_rejected') {
      set(state => ({
        myLoans: state.myLoans.filter(i => i.id !== inventoryId),
        loanableItems: state.loanableItems.map(i =>
          i.id === inventoryId ? { ...i, status: 'available' as ItemStatus, loaned_to_id: null } : i),
      }));
    } else if (type === 'returned') {
      set(state => ({
        myLentOut: state.myLentOut.filter(i => i.id !== inventoryId),
        items: state.items.map(i =>
          i.id === inventoryId
            ? { ...i, status: 'available' as ItemStatus, loaned_to_id: null, loaned_at: null, due_date: null }
            : i),
        loanableItems: state.loanableItems.map(i =>
          i.id === inventoryId ? { ...i, status: 'available' as ItemStatus, loaned_to_id: null } : i),
      }));
    } else if (type === 'loan_request') {
      set(state => ({
        items: state.items.map(i =>
          i.id === inventoryId ? { ...i, status: 'pending' as ItemStatus, loaned_to_id: fromUserId } : i),
        loanableItems: state.loanableItems.map(i =>
          i.id === inventoryId ? { ...i, status: 'pending' as ItemStatus, loaned_to_id: fromUserId } : i),
      }));
    }
  },

  subscribeToLoanStatus: (userId) => {
    const channel = supabase
      .channel(`user:${userId}`)
      .on('broadcast', { event: 'inventory_change' }, () => {
        get().fetchMyLoans(userId);
        get().fetchMyLentOut(userId);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
}));
